import { VisitorEvent } from './../../models/visitor.event';
import { ConfigService } from './../../../../common/services/config/config.service';
import { GoogleMapService } from './../../services/google.map.service';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { delay, map, Observable, Subject, takeUntil, firstValueFrom, combineLatest, of } from 'rxjs';
import {
  GoogleMap,
  MapDirectionsService,
  MapInfoWindow,
  MapMarker,
  MapMarkerClusterer,
} from '@angular/google-maps';
import { VisitorPlace } from '../../models/visitor.place';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { FancyPlaceDetailsModalComponent } from 'src/app/modules/place-calendar/components/place.details.modal/fancy.place.details.modal/fancy.place.details.modal.component';
import { IPlaceDetailsModalData } from 'src/app/modules/place-calendar/components/place.details.modal/place.details.modal.provider';
import { FancyEventDetailsModalComponent } from 'src/app/modules/event-calendar/components/event.details.modal/fancy.event.details.modal/fancy.event.details.modal.component';
import { IEventDetailsModalData } from 'src/app/modules/event-calendar/components/event.details.modal/event.details.modal.provider';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { IMapMarkerWithCustomData } from '../../models/place.marker';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';

export interface IClusterPosition {
  cluster: any;
  rect: DOMRect;
  infowindow: google.maps.InfoWindow | null;
  isInfoWindowOpen: boolean;
  marker: google.maps.Marker | null;
  el: HTMLElement;
}

@Component({
  selector: 'ig-google-map',
  templateUrl: './google.map.component.html',
  styleUrls: ['./google.map.component.scss'],
  host: {
    class: 'ig-google-map',
  },
  encapsulation: ViewEncapsulation.None,
})
export class GoogleMapComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') hostClass = 'w-full block';

  // Component state - Enhanced with detailed tracking
  private pendingMarkers: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[] = [];
  private mapInitialized = false;
  private apiReady = false;
  private initializationAttempted = false;
  private initializationComplete = false;
  private readonly destroy$ = new Subject<void>();
  private debugMode = true; // Set to false in production

  // Observables
  apiLoaded$: Observable<boolean>;
  directionsResults$: Observable<google.maps.DirectionsResult | undefined>;

  // Inputs
  @Input() useCluster = false; // Disabled by default to prevent ByteString errors
  @Input() extraFitBounds = true;
  @Input() zoom = 14;
  @Input() useDefaultMarkerClickStyle = true;
  @Input() center: google.maps.LatLngLiteral = ConfigService.config.google.USCenter;

  // Enhanced markers handling
  _markers: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[] = [];
  @Input() set markers(value: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[]) {
    this.debug('Setting markers:', value?.length || 0);
    
    const validatedMarkers = this.validateMarkers(value || []);
    this._markers = validatedMarkers;
    this.pendingMarkers = [...validatedMarkers];

    // Only process if everything is ready
    if (this.isFullyReady()) {
      this.debug('Component fully ready, processing markers immediately');
      this.processMarkersWithDelay();
    } else {
      this.debug('Component not ready, markers queued. Status:', this.getComponentStatus());
    }
  }

  // Mode with proper options update
  _mode: 'light' | 'dark' = 'light';
  @Input() set mode(value: 'light' | 'dark') {
    this._mode = value;
    if (this.mapInitialized) {
      this.updateMapOptions();
    }
  }

  // Outputs
  @Output() public onCloseInfoWindow = new EventEmitter();
  @Output() public onInfoWindowClick = new EventEmitter<VisitorEvent | VisitorPlace>();
  @Output() public onDetailsWindowClose = new EventEmitter<VisitorEvent | VisitorPlace>();
  @Output() public onclick = new EventEmitter();
  @Output() public onMarkerClick = new EventEmitter<MapMarker>();

  // UI state
  isShowEventInfoWindow = false;
  isShowPlaceInfoWindow = false;
  isShowClusterInfoWindow = false;
  isMobile = false;

  // Map configuration - Safer defaults
  options: google.maps.MapOptions;
  directionsMarkerOptions: google.maps.DirectionsRendererOptions = {
    markerOptions: { opacity: 0 },
    suppressMarkers: true,
  };

  // Component references
  @ViewChild(GoogleMap, { static: false }) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow!: MapInfoWindow;
  @ViewChildren(MapMarker) mapMarkerObjs!: QueryList<MapMarker>;
  @ViewChild(MapMarkerClusterer) markerCluster!: MapMarkerClusterer;

  // Selected marker state
  selectedMarker: {
    markerEle: MapMarker;
    marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>;
  } | null = null;

  // Cluster state
  clusterPositions: IClusterPosition[] = [];

  constructor(
    public googleMapService: GoogleMapService,
    public modalService: ModalService,
    public mapDirectionsService: MapDirectionsService,
    public scriptService: ScriptService,
    public mediaQueryService: MediaQueryService,
    private _placeService: VisitorPlaceService,
    private _renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.debug('Constructor: Initializing component');
    
    // Remove the competing script load from constructor
    // The GoogleMapService will handle script loading properly
    
    // Force disable clustering to prevent ByteString errors
    this.useCluster = false;
    
    this.initializeComponent();
  }

  private debug(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(`🗺️ [GoogleMap] ${message}`, ...args);
    }
  }

  private async initializeComponent(): Promise<void> {
    try {
      this.debug('Starting component initialization');
      
      // Setup mobile detection
      this.setupMobileDetection();
      
      // Initialize safe map options first
      this.initializeMapOptions();
      
      this.debug('Component initialization completed');
    } catch (error) {
      console.error('❌ [GoogleMap] Component initialization failed:', error);
      this.handleInitializationError(error);
    }
  }

  private setupMobileDetection(): void {
    this.mediaQueryService.isMobile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
        this.debug('Mobile state changed:', isMobile);
      });
  }

  ngOnInit(): void {
    this.debug('ngOnInit started');
    
    // Initialize with ultra-safe options
    this.initializeMapOptions();
    
    // Ensure clustering is disabled
    this.useCluster = false;
    
    this.debug('ngOnInit completed');
  }

  private initializeMapOptions(): void {
    this.debug('Initializing ultra-safe map options');
    
    // Ultra-safe options that won't cause ByteString errors
    this.options = {
      center: this.center,
      zoom: this.zoom,
      // Minimal UI controls to prevent issues
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      // Safe interaction settings
      gestureHandling: 'auto',
      clickableIcons: false, // Critical: prevents ByteString issues
      // NO mapId - major ByteString cause
      // NO styles - can cause ByteString issues
      // NO custom renderingType
    };
    
    this.debug('Ultra-safe map options initialized');
  }

  private updateMapOptions(): void {
    if (this.options && this.mapInitialized) {
      this.debug('Updating map options');
      
      // Only update safe properties
      const safeUpdate = {
        ...this.options,
        clickableIcons: false // Keep disabled
      };
      
      this.options = safeUpdate;
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.debug('ngAfterViewInit - Starting initialization sequence');
    
    // Use NgZone to handle async operations properly
    this.ngZone.runOutsideAngular(() => {
      this.startInitializationSequence();
    });
  }

  private async startInitializationSequence(): Promise<void> {
    if (this.initializationAttempted) {
      this.debug('Initialization already attempted, skipping');
      return;
    }

    this.initializationAttempted = true;
    this.debug('Starting initialization sequence');

    try {
      // Step 1: Wait for Google Maps API to load
      await this.waitForGoogleMapsAPI();
      
      // Step 2: Wait for Angular Google Maps component to initialize  
      await this.waitForMapComponentReady();
      
      // Step 3: Mark as fully initialized
      this.markAsInitialized();
      
      // Step 4: Process any pending markers
      this.ngZone.run(() => {
        this.processPendingMarkers();
      });
      
    } catch (error) {
      console.error('❌ [GoogleMap] Initialization sequence failed:', error);
      this.handleInitializationError(error);
    }
  }

  private async waitForGoogleMapsAPI(): Promise<void> {
    this.debug('Step 1: Waiting for Google Maps API to load...');
    
    try {
      // Start loading the API
      this.apiLoaded$ = this.googleMapService.loadGoogleMapApi();
      
      // Wait for it to complete with timeout
      const apiLoaded = await firstValueFrom(this.apiLoaded$);
      
      if (!apiLoaded) {
        throw new Error('Google Maps API failed to load');
      }
      
      // Double-check API is actually ready
      const apiReady = await this.googleMapService.waitForApiReady(10000);
      if (!apiReady) {
        throw new Error('Google Maps API not ready after loading');
      }
      
      this.apiReady = true;
      this.debug('✅ Step 1 completed: Google Maps API loaded and ready');
      
    } catch (error) {
      this.debug('❌ Step 1 failed: Google Maps API loading error:', error);
      throw error;
    }
  }

  private async waitForMapComponentReady(): Promise<void> {
    this.debug('Step 2: Waiting for Angular Google Maps component...');
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Map component initialization timeout'));
      }, 15000);

      const checkMapComponent = () => {
        // Check if the Angular Google Maps component is ready
        if (this.googleMap?.googleMap) {
          clearTimeout(timeout);
          this.mapInitialized = true;
          this.debug('✅ Step 2 completed: Angular Google Maps component ready');
          resolve();
        } else {
          // Keep checking every 100ms
          setTimeout(checkMapComponent, 100);
        }
      };
      
      checkMapComponent();
    });
  }

  private markAsInitialized(): void {
    this.debug('Step 3: Marking component as fully initialized');
    this.initializationComplete = true;
    
    // Force change detection to update template
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
    
    this.debug('✅ Step 3 completed: Component fully initialized');
  }

  private processPendingMarkers(): void {
    this.debug('Step 4: Processing pending markers');
    
    if (this.pendingMarkers.length > 0) {
      this.debug(`Processing ${this.pendingMarkers.length} pending markers`);
      this.processMarkersWithDelay();
    } else {
      this.debug('No pending markers to process');
    }
    
    this.debug('✅ Step 4 completed: Pending markers processed');
  }

  private validateMarkers(markers: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[]): IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>[] {
    if (!markers || !Array.isArray(markers)) {
      this.debug('Invalid markers array provided');
      return [];
    }

    const validMarkers = markers.filter(marker => {
      if (!marker?.position) {
        console.warn('⚠️ [GoogleMap] Marker missing position:', marker);
        return false;
      }
      
      const { lat, lng } = marker.position;
      if (typeof lat !== 'number' || typeof lng !== 'number' || 
          isNaN(lat) || isNaN(lng) ||
          lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        console.warn('⚠️ [GoogleMap] Invalid marker coordinates:', marker);
        return false;
      }
      
      return true;
    });

    this.debug(`Validated ${validMarkers.length}/${markers.length} markers`);
    return validMarkers;
  }

  private isFullyReady(): boolean {
    const ready = this.mapInitialized && this.apiReady && this.initializationComplete && !!this.googleMap?.googleMap;
    this.debug('Full readiness check:', ready);
    return ready;
  }

  private getComponentStatus(): object {
    return {
      mapInitialized: this.mapInitialized,
      apiReady: this.apiReady,
      initializationComplete: this.initializationComplete,
      hasGoogleMap: !!this.googleMap?.googleMap,
      initializationAttempted: this.initializationAttempted,
      pendingMarkersCount: this.pendingMarkers.length,
      currentMarkersCount: this._markers.length
    };
  }

  private processMarkersWithDelay(): void {
    if (!this.isFullyReady()) {
      this.debug('Not ready to process markers, skipping');
      return;
    }

    this.debug('Processing markers with safety delay');

    // Clean markers first to prevent ByteString errors
    this.cleanMarkersForSafety();

    // Process with delay to ensure stability
    setTimeout(() => {
      this.ngZone.run(() => {
        try {
          if (this._markers.length > 0) {
            this.debug(`Processing ${this._markers.length} markers`);
            this.handleMapBounds();
          } else {
            this.debug('No markers to process, setting no content state');
            this.setNoContentState();
          }
          
          // Clear pending markers
          this.pendingMarkers = [];
          
        } catch (error) {
          console.error('❌ [GoogleMap] Error processing markers:', error);
        }
      });
    }, 300); // Shorter delay for better UX
  }

  private cleanMarkersForSafety(): void {
    this.debug('Cleaning markers for safety');
    
    this._markers.forEach(marker => {
      // Ensure safe options
      if (marker.options) {
        // Create completely clean options
        const cleanOptions: any = {};
        
        // Only copy absolutely safe properties
        if (typeof marker.options.zIndex === 'number') {
          cleanOptions.zIndex = marker.options.zIndex;
        }
        if (typeof marker.options.opacity === 'number') {
          cleanOptions.opacity = marker.options.opacity;
        }
        if (typeof marker.options.visible === 'boolean') {
          cleanOptions.visible = marker.options.visible;
        }
        
        // Replace with clean options
        marker.options = cleanOptions;
      } else {
        marker.options = {};
      }
      
      // Ensure safe title
      if (!marker.title || typeof marker.title !== 'string' || marker.title.trim() === '') {
        marker.title = 'Location';
      }
      
      // Clean or remove problematic label
      if (marker.label) {
        if (typeof marker.label === 'object') {
          marker.label = {
            text: String((marker.label as any).text || 'M').substring(0, 1),
            color: '#000000',
            fontSize: '12px'
          };
        } else if (typeof marker.label === 'string' && marker.label.trim() === '') {
          marker.label = undefined;
        }
      }
    });
    
    this.debug('Markers cleaned for safety');
  }

  private handleMapBounds(): void {
    try {
      if (!this.googleMap?.googleMap) {
        console.warn('⚠️ [GoogleMap] Cannot handle bounds - map not ready');
        return;
      }

      if (this._markers.length === 1) {
        // Single marker - center on it with appropriate zoom
        const marker = this._markers[0];
        this.center = {
          lat: marker.position.lat as number,
          lng: marker.position.lng as number,
        };
        
        // Set appropriate zoom for single marker
        setTimeout(() => {
          if (this.googleMap?.googleMap) {
            this.googleMap.googleMap.setZoom(14);
          }
        }, 100);
        
        this.debug('Centered on single marker');
        
      } else if (this._markers.length > 1) {
        // Multiple markers - create bounds
        this.fitMarkersInBounds();
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error handling map bounds:', error);
    }
  }

  private fitMarkersInBounds(): void {
    try {
      const bounds = new google.maps.LatLngBounds();
      let validMarkersCount = 0;
      
      this._markers.forEach((marker) => {
        if (marker.position && 
            typeof marker.position.lat === 'number' && 
            typeof marker.position.lng === 'number' &&
            !isNaN(marker.position.lat) &&
            !isNaN(marker.position.lng)) {
          
          bounds.extend(new google.maps.LatLng(marker.position.lat, marker.position.lng));
          validMarkersCount++;
        }
      });
      
      if (validMarkersCount > 1 && this.extraFitBounds) {
        // Add padding to prevent markers being too close to edges
        this.googleMap.fitBounds(bounds, 50);
        this.debug(`Fitted bounds for ${validMarkersCount} markers`);
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error fitting bounds:', error);
    }
  }

  private setNoContentState(): void {
    this.center = ConfigService.config.google.USCenter as google.maps.LatLngLiteral;
    this.options = {
      ...this.options,
      zoom: 5,
    };
    this.debug('Set no content state');
  }

  private handleInitializationError(error: any): void {
    console.error('❌ [GoogleMap] Initialization error:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Failed to initialize map';
    this.debug('Initialization failed:', errorMessage);
    
    // Reset state for potential retry
    this.resetComponentState();
    
    // You might want to emit an error event or show user notification here
    // this.onInitializationError.emit(errorMessage);
  }

  private resetComponentState(): void {
    this.debug('Resetting component state for retry');
    
    this.mapInitialized = false;
    this.apiReady = false;
    this.initializationAttempted = false;
    this.initializationComplete = false;
    this.pendingMarkers = [...this._markers];
  }

  // Map interaction methods
  mapClick(event: google.maps.MapMouseEvent): void {
    this.closeInfoWindow();
    this.onclick.emit();
    this.closeClusterInfoWindows();
  }

  markerClick(markerEle: MapMarker, marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>): void {
    try {
      this.debug('Marker click handled safely');
      
      if (this.useDefaultMarkerClickStyle) {
        this.updateMarkerStyles(marker);
      }

      this.selectedMarker = { markerEle, marker };
      this.onMarkerClick.emit(markerEle);

      // Determine which info window to show based on data type
      if (marker.data && 'eventLink' in marker.data) {
        this.showEventInfoWindow(markerEle, marker as IMapMarkerWithCustomData<VisitorEvent>);
      } else {
        this.showPlaceInfoWindow(markerEle, marker as IMapMarkerWithCustomData<VisitorPlace>);
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error in marker click:', error);
    }
  }

  private updateMarkerStyles(selectedMarker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>): void {
    try {
      this.debug('Updating marker styles safely');
      
      // Reset all markers to default state (safe properties only)
      this._markers.forEach((marker) => {
        if (marker.options) {
          // Remove ALL potentially problematic properties
          delete (marker.options as any).icon;
          delete (marker.options as any).animation;
          delete (marker.options as any).shape;
          delete (marker.options as any).symbol;
          
          // Set only safe properties
          marker.options = {
            zIndex: 1,
            opacity: 1
          };
        }
      });
      
      // Highlight selected marker (safely)
      if (selectedMarker.options) {
        selectedMarker.options = {
          zIndex: 999,
          opacity: 1
          // NO icon, animation, or other problematic properties
        };
      }
      
      // Update center
      this.center = {
        lat: selectedMarker.position.lat,
        lng: selectedMarker.position.lng,
      } as google.maps.LatLngLiteral;
      
      this.debug('Marker styles updated safely');
    } catch (error) {
      console.error('❌ [GoogleMap] Error updating marker styles:', error);
    }
  }

  // Info window methods
  showEventInfoWindow(marker?: MapMarker, data?: IMapMarkerWithCustomData<VisitorEvent>): void {
    this.isShowPlaceInfoWindow = false;
    this.isShowEventInfoWindow = true;
    
    if (marker && data) {
      this.selectedMarker = { markerEle: marker, marker: data };
    }
    
    if (this.selectedMarker) {
      this.infoWindow.open(this.selectedMarker.markerEle);
    }
  }

  showPlaceInfoWindow(marker?: MapMarker, data?: IMapMarkerWithCustomData<VisitorPlace>): void {
    this.isShowEventInfoWindow = false;
    this.isShowPlaceInfoWindow = true;
    
    if (marker && data) {
      this.selectedMarker = { markerEle: marker, marker: data };
    }
    
    if (this.selectedMarker) {
      this.infoWindow.open(this.selectedMarker.markerEle);
    }
  }

  closeInfoWindow(): void {
    this.isShowEventInfoWindow = false;
    this.isShowPlaceInfoWindow = false;
    this.infoWindow?.close();
    this.onCloseInfoWindow.emit();
  }

  // Navigation methods
  goToEvent(event: VisitorEvent): void {
    try {
      const modal = this.modalService.show({
        component: FancyEventDetailsModalComponent,
        panelClass: 'ig-modal-w-full',
        data: {
          currentEvent: event,
          allEvents: [],
          favCustomEvents: [],
          from: 'placeCalendar',
        } as IEventDetailsModalData,
      });
      
      this.onInfoWindowClick.emit(event);

      modal.beforeClosed().subscribe(() => {
        this.onDetailsWindowClose.emit(event);
      });
    } catch (error) {
      console.error('❌ [GoogleMap] Error opening event modal:', error);
    }
  }

  goToPlace(place: VisitorPlace): void {
    try {
      if (place.isManuallyCreated || place.socialName) {
        const modal = this.modalService.show({
          component: FancyPlaceDetailsModalComponent,
          panelClass: 'ig-modal-w-full',
          data: {
            currentPlace: place,
          } as IPlaceDetailsModalData,
        });

        modal.beforeClosed().subscribe(() => {
          this.onDetailsWindowClose.emit(place);
        });
      } else if (place.website) {
        window.open(place.website, '_blank');
      }
      
      this.onInfoWindowClick.emit(place);
    } catch (error) {
      console.error('❌ [GoogleMap] Error opening place modal:', error);
    }
  }

  // Directions methods
  async drawDirectionRoute(
    locations: { lat: number; lng: number; stopover?: boolean }[] = [],
    travelMode = google.maps.TravelMode.DRIVING,
    option: { optimizeWaypoints: boolean } = { optimizeWaypoints: false }
  ): Promise<void> {
    if (!locations || locations.length <= 1) {
      console.warn('⚠️ [GoogleMap] Invalid locations for directions');
      return;
    }

    // Wait for component to be fully ready
    if (!this.isFullyReady()) {
      console.warn('⚠️ [GoogleMap] Component not ready for directions');
      return;
    }

    try {
      const waypoints = locations.slice(1, -1).map((location) => ({
        location: { lng: location.lng, lat: location.lat },
        stopover: location.stopover ?? true,
      }));

      const request: google.maps.DirectionsRequest = {
        destination: locations[locations.length - 1],
        origin: locations[0],
        waypoints: waypoints,
        travelMode: travelMode,
        ...option,
      };

      this.directionsResults$ = this.mapDirectionsService
        .route(request)
        .pipe(map((response) => response.result));
    } catch (error) {
      console.error('❌ [GoogleMap] Error drawing direction route:', error);
    }
  }

  // Utility methods
  getPixelPositionByLatLng(latlng: google.maps.LatLng): Promise<{ x: number; y: number }> {
    return new Promise<{ x: number; y: number }>((resolve, reject) => {
      const overlay = new google.maps.OverlayView();
      overlay.draw = function () {};
      overlay.onAdd = function () {
        const projection = this.getProjection();
        const pixel = projection.fromLatLngToContainerPixel(latlng);
        resolve(pixel);
      };
      overlay.setMap(this.googleMap.googleMap);
    });
  }

  getDisplayCategory(place: VisitorPlace): string {
    return this._placeService.getDisplayCategory(place);
  }

  public fitBounds(padding?: number): void {
    try {
      if (!this.isFullyReady()) {
        console.warn('⚠️ [GoogleMap] Cannot fit bounds - component not ready');
        return;
      }

      this.handleMapBounds();
    } catch (error) {
      console.error('❌ [GoogleMap] Error fitting bounds:', error);
    }
  }

  // Clustering methods (kept but safer)
  clustererEnd($event: any): void {
    try {
      this.closeClusterInfoWindows();
      this.getPositionOfEachCluster($event);

      if (!this.isMobile) {
        this.removeClusterListener();
        this.setupClusterHoverListeners();
      }
    } catch (error) {
      console.error('❌ [GoogleMap] Error handling cluster end:', error);
    }
  }

  private closeClusterInfoWindows(): void {
    this.clusterPositions.forEach(clusterPosition => {
      if (clusterPosition.infowindow) {
        clusterPosition.infowindow.close();
        clusterPosition.infowindow = null;
        clusterPosition.isInfoWindowOpen = false;
      }
      if (clusterPosition.marker) {
        clusterPosition.marker.setMap(null);
        clusterPosition.marker = null;
      }
    });

    this.isShowClusterInfoWindow = false;
  }

  private getPositionOfEachCluster($event: any): void {
    setTimeout(() => {
      this.clusterPositions = [];
      $event.clusters_?.forEach((cluster: any) => {
        if (cluster.clusterIcon_?.div_) {
          const rect = cluster.clusterIcon_.div_.getBoundingClientRect();
          this.clusterPositions.push({
            cluster,
            rect,
            infowindow: null,
            isInfoWindowOpen: false,
            marker: null,
            el: cluster.clusterIcon_.div_,
          });
        }
      });
    }, 100);
  }

  private setupClusterHoverListeners(): void {
    setTimeout(() => {
      const clusters = this.shadowRoot?.querySelectorAll('.ig-google-map .cluster');
      clusters?.forEach((cluster) => {
        const rect = cluster.getBoundingClientRect();
        cluster.addEventListener('mouseenter', () => {
          this.handleClusterHover(rect);
        });
      });
    }, 200);
  }

  private handleClusterHover(rect: DOMRect): void {
    const matchingCluster = this.clusterPositions.find(
      clusterPosition => 
        clusterPosition.rect.x === rect.x && 
        clusterPosition.rect.y === rect.y
    );

    if (matchingCluster && !matchingCluster.isInfoWindowOpen) {
      this.showClusterInfoWindow(matchingCluster);
    }

    // Close other cluster info windows
    this.clusterPositions.forEach(clusterPosition => {
      if (clusterPosition !== matchingCluster && clusterPosition.infowindow) {
        clusterPosition.infowindow.close();
        clusterPosition.infowindow = null;
        clusterPosition.isInfoWindowOpen = false;
      }
    });
  }

  private showClusterInfoWindow(clusterPosition: IClusterPosition): void {
    try {
      this.isShowClusterInfoWindow = true;
      const container = this.generateClusterHoveringPopupContent(clusterPosition);
      this.generateAndShowClusterInfoWindow(container, clusterPosition);
    } catch (error) {
      console.error('❌ [GoogleMap] Error showing cluster info window:', error);
    }
  }

  private generateAndShowClusterInfoWindow(
    container: HTMLDivElement,
    clusterPosition: IClusterPosition
  ): void {
    const infowindow = new google.maps.InfoWindow({
      content: container,
      maxWidth: window.innerWidth * 0.2,
      minWidth: window.innerWidth * 0.2,
    });

    const marker = new google.maps.Marker({
      position: {
        lat: clusterPosition.cluster.center_.lat(),
        lng: clusterPosition.cluster.center_.lng(),
      },
      map: this.googleMap.googleMap,
      opacity: 0,
    });

    infowindow.open({
      anchor: marker,
      map: this.googleMap.googleMap,
    });

    clusterPosition.infowindow = infowindow;
    clusterPosition.isInfoWindowOpen = true;
    clusterPosition.marker = marker;

    infowindow.addListener('closeclick', () => {
      this.isShowClusterInfoWindow = false;
      clusterPosition.infowindow = null;
      clusterPosition.isInfoWindowOpen = false;
      if (marker) {
        marker.setMap(null);
      }
    });
  }

  private generateClusterHoveringPopupContent(clusterPosition: IClusterPosition): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'p-4';
    
    const grid = document.createElement('div');
    const gridCols = clusterPosition.cluster.markers_.length === 2 ? 2 : 3;
    grid.className = `grid grid-cols-${gridCols} bg-white gap-3`;
    
    clusterPosition.cluster.markers_.slice(0, 6).forEach((marker: any) => {
      const placeMarker = this._markers.find((m) => m.data._id === marker.title);

      if (placeMarker) {
        const div = document.createElement('div');
        div.innerHTML = `
          <div class="flex flex-col gap-2 items-center overflow-hidden cursor-pointer group">
            <img src="${placeMarker.data.cover?.source || ''}" class="w-full h-[8vh] rounded-md group-hover:scale-[1.05] transition-all" />
            <p class="text-sm group-hover:font-semibold group-hover:text-gray-600 transition-all">${placeMarker.data.title || 'Untitled'}</p>
          </div>
        `;
        
        this._renderer.listen(div, 'click', () => {
          if ('startTime' in placeMarker.data) {
            this.goToEvent(placeMarker.data as VisitorEvent);
          } else {
            this.goToPlace(placeMarker.data as VisitorPlace);
          }
        });
        
        this._renderer.appendChild(grid, div);
      }
    });

    this._renderer.appendChild(container, grid);
    
    if (clusterPosition.cluster.markers_.length > 6) {
      const moreDiv = document.createElement('div');
      moreDiv.innerHTML = `
        <div class="flex flex-col gap-2 items-center overflow-hidden mt-4">
          <p class="text-sm bg-blue-400 text-white px-3 py-1.5 rounded-sm uppercase cursor-pointer font-semibold">Click to see more</p>
        </div>
      `;

      this._renderer.listen(moreDiv, 'click', () => {
        clusterPosition.el?.click();
      });

      this._renderer.appendChild(container, moreDiv);
    }
    
    return container;
  }

  private removeClusterListener(): void {
    // This is a placeholder - you'd need to store actual listener references
    const clusters = this.shadowRoot?.querySelectorAll('.cluster');
    clusters?.forEach((cluster) => {
      cluster.removeEventListener('mouseenter', () => {});
    });
  }



  setNoContent(): void {
    this.center = ConfigService.config.google.USCenter as google.maps.LatLngLiteral;
    this.options = {
      ...this.options,
      zoom: 5,
    };
  }


  // Getters for shadow DOM access
  public get imgoingWrapper(): HTMLElement | null {
    return (
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix')
    );
  }

  public get shadowRoot(): ShadowRoot | null {
    const wrapper = this.imgoingWrapper || document;
    const appRoot = wrapper.querySelector('app-root') as any;
    return appRoot?.shadowRoot || null;
  }

  // Cleanup
  ngOnDestroy(): void {
    this.debug('Component destroying');
    
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up cluster info windows
    this.closeClusterInfoWindows();
    
    // Clean up selected marker
    this.selectedMarker = null;
    
    // Reset service if needed
    // this.googleMapService.reset();
  }

  // Public methods for external use
  public retryInitialization(): void {
    this.debug('Retrying initialization');
    this.resetComponentState();
    this.startInitializationSequence();
  }

  public getHealthStatus(): {
    mapInitialized: boolean;
    apiReady: boolean;
    initializationComplete: boolean;
    markersCount: number;
    pendingMarkersCount: number;
    isFullyReady: boolean;
  } {
    return {
      mapInitialized: this.mapInitialized,
      apiReady: this.apiReady,
      initializationComplete: this.initializationComplete,
      markersCount: this._markers.length,
      pendingMarkersCount: this.pendingMarkers.length,
      isFullyReady: this.isFullyReady(),
    };
  }

  public forceProcessMarkers(): void {
    this.debug('Force processing markers');
    if (this.isFullyReady()) {
      this.processMarkersWithDelay();
    } else {
      console.warn('⚠️ [GoogleMap] Cannot force process markers - component not ready');
    }
  }

  // TrackBy function for markers to improve performance
  trackByMarkerId(index: number, marker: IMapMarkerWithCustomData<VisitorEvent | VisitorPlace>): any {
    return marker.data?._id || marker.data?.id || index;
  }

  // Method to check if all required services are loaded
  private areServicesReady(): boolean {
    return !!(
      this.googleMapService &&
      this.modalService &&
      this.mapDirectionsService &&
      this._placeService
    );
  }

  // Method to validate component dependencies
  private validateDependencies(): boolean {
    const missingServices = [];
    
    if (!this.googleMapService) missingServices.push('GoogleMapService');
    if (!this.modalService) missingServices.push('ModalService');
    if (!this.mapDirectionsService) missingServices.push('MapDirectionsService');
    if (!this._placeService) missingServices.push('VisitorPlaceService');

    if (missingServices.length > 0) {
      console.error('[GoogleMap] Missing required services:', missingServices);
      return false;
    }

    return true;
  }

  // Enhanced error handling for template operations
  handleTemplateError(error: any, operation: string): void {
    console.error(`[GoogleMap] Template error in ${operation}:`, error);
    
    // You can add more sophisticated error handling here
    // For example, showing a notification to the user
    // this.notificationService.showError(`Map ${operation} failed`);
  }

  // Safe method to access nested properties
  safeGetProperty<T>(obj: any, path: string, defaultValue: T): T {
    try {
      return path.split('.').reduce((o, p) => o && o[p], obj) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }
}